/**
 * useServiceCheckout — hook con toda la lógica de estado + handlers del
 * ServiceCheckoutModal (SPEC-244 · §Sprint5 partición VERBATIM de
 * `frontend/src/components/market/checkout-modal.tsx` 2.637 L).
 *
 * Extrae VERBATIM toda la lógica de:
 *   - State declarations (step, reservationDate, reservationTimeStart,
 *     reservationTimeEnd, guestCount, submitting, submitError)
 *     [2FA state removed per Founder directive 2026-11: checkout no
 *     longer requires 2FA]
 *   - INV-SEC-03 idempotencyKeyRef (safeUUID, reused within a modal
 *     session — fresh UUID on each open)
 *   - depositPercent (from `product.serviceConfig.depositPercent`, falls
 *     back to `SERVICE_DEPOSIT_PERCENT_FALLBACK` per SPEC-NEGOCIO §10.7)
 *   - grandTotal + depositAmount + balanceAmount useMemos (50/50 split
 *     per SPEC §10.7)
 *   - useEffect reset on `open` change
 *   - detailsValid useMemo (Step 1 validation — date + time range +
 *     guestCount ≥1 integer + end > start)
 *   - callCreate (POST /api/market/orders with productType='service';
 *     SPEC-157 SINGLE_PURCHASE_ALREADY_BOUGHT intercept)
 *   - handleOpenChange (anti-P-043 — block close during submit)
 *
 * Transformaciones documentadas (las ÚNICAS):
 *   - T-HOOK-EXTRACTION: la lógica se mueve del cuerpo del componente a un
 *     hook personalizado `useServiceCheckout({ open, onClose, product })`
 *     que retorna todo el state + handlers.
 *   - T-IMPORTS: recalcula imports (solo lo que el hook usa).
 *
 * §6 Anti-Invención: NO business logic. Anti-P-371: cero cambios.
 * Anti-P-041: 0 `as any` (the `as Route` cast is in the view, not here).
 * Anti-P-042: 0 `console.*`. Anti-P-043: submitting state blocks close.
 */

'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { api, ApiClientError } from '@/lib/api-client';
import { safeUUID } from '@/lib/utils';
import type { Route } from 'next';
import type {
  PublicProductResponse,
  OrderCreateRequestService,
  OrderResponse,
} from '@profileia/shared';
import { mapCreateError } from '../helpers';
import { SERVICE_DEPOSIT_PERCENT_FALLBACK } from '../constants';
import type { Step } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseServiceCheckoutArgs {
  open: boolean;
  onClose: () => void;
  product: PublicProductResponse;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useServiceCheckout({ open, onClose, product }: UseServiceCheckoutArgs) {
  const router = useRouter();
  const [step, setStep] = useState<Step>('details');
  const [reservationDate, setReservationDate] = useState<string>('');
  const [reservationTimeStart, setReservationTimeStart] = useState<string>('');
  const [reservationTimeEnd, setReservationTimeEnd] = useState<string>('');
  const [guestCount, setGuestCount] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // INV-SEC-03: idempotency key — same pattern as the physical variant.
  const idempotencyKeyRef = useRef<string>(safeUUID());

  // Service deposit % — from the product's `serviceConfig.depositPercent`
  // (snapshot at product-create time). Falls back to 50 (SPEC §10.7
  // baseline) only if `serviceConfig` is missing (defense in depth —
  // Fase 3 pre-validate hook requires it for `type='service'`).
  const depositPercent = product.serviceConfig?.depositPercent ?? SERVICE_DEPOSIT_PERCENT_FALLBACK;

  // The service grandTotal = product.priceCredits (services don't have
  // variations, quantities, or shipping — INV-MKT-14 single-unit booking).
  const grandTotal = parseFloat(product.priceCredits);
  const depositAmount = useMemo(
    () => (grandTotal * depositPercent) / 100,
    [grandTotal, depositPercent],
  );
  const balanceAmount = useMemo(
    () => grandTotal - depositAmount,
    [grandTotal, depositAmount],
  );

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      setStep('details');
      setReservationDate('');
      setReservationTimeStart('');
      setReservationTimeEnd('');
      setGuestCount(1);
      setSubmitting(false);
      setSubmitError(null);
      idempotencyKeyRef.current = safeUUID();
    }
  }, [open]);

  // ─── Validation ──────────────────────────────────────────────────────────

  // Step 1 validation — all 4 fields required + end > start.
  const detailsValid = useMemo(() => {
    if (!reservationDate) return false;
    if (!reservationTimeStart) return false;
    if (!reservationTimeEnd) return false;
    if (guestCount < 1 || !Number.isInteger(guestCount)) return false;
    // End time must be strictly after start time (HH:mm string comparison
    // works because ISO 8601 time strings sort lexicographically when
    // zero-padded — the <input type="time"> always emits HH:mm).
    if (reservationTimeEnd <= reservationTimeStart) return false;
    // Reservation date must be today or in the future (the backend
    // `SERVICE_RESERVATION_PAST` check is more strict — the buyer must
    // book at least 1 day in advance per the service flow §4.1 step 1).
    // The frontend lets the user pick today (the backend will reject
    // past-dates with a friendly error). Defense in depth: don't block
    // a legitimate "tomorrow morning" booking at the UI layer.
    return true;
  }, [reservationDate, reservationTimeStart, reservationTimeEnd, guestCount]);

  // ─── Submit handler ──────────────────────────────────────────────────────

  const callCreate = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: OrderCreateRequestService = {
        productType: 'service',
        productId: product.productId,
        guestCount,
        reservationDate,
        reservationTimeRange: {
          start: reservationTimeStart,
          end: reservationTimeEnd,
        },
      };

      const result: OrderResponse = await api.market.orders.create(payload, {
        idempotencyKey: idempotencyKeyRef.current,
      });

      // Sprint 139 (PR-H): services don't have a shipping address (no ship
      // method), so there's no address to save to the address book. The
      // maybeSaveAddressForNextTime helper is only in PhysicalCheckoutModal
      // where the shipping address form lives. Services skip this step.

      toast.success('¡Reserva creada!', {
        description: `Tu reserva ${result.orderNumber} está en espera de confirmación del vendedor (72h).`,
      });
      router.push(`/market/ordenes/${result.orderNumber}` as Route);
      onClose();
    } catch (err) {
      // SPEC-157: intercept 409 SINGLE_PURCHASE_ALREADY_BOUGHT — servicio
      // recurrente (suscripción / membresía / consultoría mensual) — el
      // comprador ya es cliente y debe coordinar pagos posteriores
      // off-platform. Info toast + close modal.
      if (err instanceof ApiClientError && err.code === 'SINGLE_PURCHASE_ALREADY_BOUGHT') {
        toast.info('Ya eres cliente de este servicio', {
          description: 'Contacta directamente al vendedor para gestionar tus pagos recurrentes.',
          duration: 6000,
        });
        onClose();
        return;
      }
      if (err instanceof ApiClientError) {
        const mapped = mapCreateError(err);
        if (mapped !== null) {
          setSubmitError(mapped);
          if (err.code === 'IDEMPOTENCY_CONFLICT') {
            idempotencyKeyRef.current = safeUUID();
          }
        } else {
          setSubmitError(err.message ?? 'Error al crear la reserva.');
        }
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      } else {
        setSubmitError('Error al crear la reserva. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o: boolean) => {
    if (!o && submitting) return;
    if (!o) onClose();
  };

  return {
    // state
    step,
    setStep,
    reservationDate,
    setReservationDate,
    reservationTimeStart,
    setReservationTimeStart,
    reservationTimeEnd,
    setReservationTimeEnd,
    guestCount,
    setGuestCount,
    submitting,
    submitError,
    setSubmitError,
    // derived
    depositPercent,
    grandTotal,
    depositAmount,
    balanceAmount,
    detailsValid,
    // handlers
    callCreate,
    handleOpenChange,
  };
}
